import { graphql } from 'react-relay';

import { useIsLivePageWithSSR } from '@confluence/live-pages-utils/entry-points/useIsLivePageWithSSR.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useRenderLivePageEarlyContentSubTypeAndStatusQuery as useRenderLivePageEarlyContentSubTypeAndStatusQuery$data,
	useRenderLivePageEarlyContentSubTypeAndStatusQueryVariables as useRenderLivePageEarlyContentSubTypeAndStatusQuery$variables,
} from './__types__/useRenderLivePageEarlyContentSubTypeAndStatusQuery.ts';
export const useRenderLivePageEarly = ({
	contentId,
	cachedSubType,
	cachedIsArchived,
	renderLivePageAsViewPage,
}: {
	contentId: string;
	cachedSubType: string | undefined;
	cachedIsArchived: boolean | undefined;
	renderLivePageAsViewPage: boolean;
}): {
	renderLivePageEarly: boolean;
	isLivePageCacheOutdated: boolean;
	isFastLoadedLivePageArchived: boolean;
	isLivePageFastLoadedFromSSR: boolean;
} => {
	const { isLive, isTypeKnown } = useIsLivePageWithSSR();

	// To handle the scenario where the cached subtype is 'live' but outdated (it's no longer 'live'): We briefly render LivePageComponent, ContentSubTypeAndStatusQuery finishes, then
	// if ContentSubTypeAndStatusQuery returns that the subtype is not actually 'live', we will render the rest of ViewPageCommon rather than rendering LivePageComponent here.
	// To handle the scenario where cached subtype is not 'live' but outdated (it's now 'live'): We'll check for it later in this component, after loading ContentUnifiedQuery,
	// and render LivePageComponent if needed.
	const { data: latestContentData, loading: isContentDataLoading } = useQueryInterop<
		useRenderLivePageEarlyContentSubTypeAndStatusQuery$data,
		useRenderLivePageEarlyContentSubTypeAndStatusQuery$variables
	>(
		graphql`
			query useRenderLivePageEarlyContentSubTypeAndStatusQuery(
				$contentId: ID!
				$versionOverride: Int
			) {
				content(id: $contentId, version: $versionOverride) {
					nodes {
						id
						status
						subType
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			// We don't need to retrieve subtype if cachedSubType is not 'live' because we'll never enter LivePageComponent early.
			skip: isTypeKnown || !(!renderLivePageAsViewPage && cachedSubType === 'live'),
			fetchPolicy: 'network-only',
		},
	);
	// Callers that render live docs through the view-page path never want LivePageComponent, so this
	// has to be checked ahead of the SSR shortcut below
	if (renderLivePageAsViewPage) {
		return {
			renderLivePageEarly: false,
			isLivePageCacheOutdated: false,
			isFastLoadedLivePageArchived: false,
			isLivePageFastLoadedFromSSR: false,
		};
	}

	// Return early if we're certain that the page is live
	if (isTypeKnown) {
		return {
			renderLivePageEarly: Boolean(isLive),
			isLivePageCacheOutdated: false,
			isFastLoadedLivePageArchived: false,
			isLivePageFastLoadedFromSSR: true,
		};
	}

	const loadedSubType = latestContentData?.content?.nodes?.[0]?.subType;
	const mostRecentSubType = isContentDataLoading ? cachedSubType : loadedSubType;
	const loadedIsArchived = latestContentData?.content?.nodes?.[0]?.status === 'archived';
	const mostRecentIsArchived = isContentDataLoading ? cachedIsArchived : loadedIsArchived;

	// If live page is archived, render through normal view page instead of live page component
	return {
		renderLivePageEarly: mostRecentSubType === 'live' && !mostRecentIsArchived,
		isLivePageCacheOutdated:
			cachedSubType === 'live' &&
			!isContentDataLoading &&
			(loadedSubType !== 'live' || (cachedIsArchived === false && loadedIsArchived === true)),
		isFastLoadedLivePageArchived:
			mostRecentSubType === 'live' && loadedIsArchived === true && !cachedIsArchived,
		isLivePageFastLoadedFromSSR: false,
	};
};
